import { Injectable, NgZone } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MsService } from './ms.service';
import { UserService } from './user.service';
import { AuthServiceApp } from './auth.service';
import Web3 from 'web3';

interface ContractDetails {
  abi: any[];
  address: string;
}

interface DynamicContracts {
  [key: string]: ContractDetails;
}
@Injectable({ providedIn: 'root' })
export class SmartWalletService {
  private isModalContainerInjecting = false;
  private contracts: DynamicContracts = {};
  private web3: Web3 = new Web3(new Web3.providers.HttpProvider(environment.rpcAddress));
  private modalContainer: HTMLDivElement | null = null;
  private pendingAuthProviderId?: string;

  // behaviors:
  public isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(!!localStorage.getItem('accessToken'));

  constructor(private msService: MsService, private userService: UserService, private authService: AuthServiceApp, private ngZone: NgZone) {
    this.isModalContainerInjecting = localStorage.getItem('isModalContainerInjecting') === 'true';
    this.injectModalContainer();
    this.isModalContainerInjecting = false;
    this.loadContracts();
  }

  private runInAngularZone(fn: () => void) {
    if (NgZone.isInAngularZone()) {
      fn();
    } else {
      this.ngZone.run(fn);
    }
  }

  loadContracts() {
    for (const [contractName, contractDetails] of Object.entries(environment.contracts)) {
      this.contracts[contractName] = contractDetails;
    }
  }

  async auth(): Promise<any> {
    if (this.isAuthenticated$.value) return true;
    this.openLoginModal();
    return true;
  }

  openLoginModal() {
    if (!this.modalContainer) return;
    this.modalContainer.classList.add('visible');
    document.body.classList.add('smart-wallet-login-opened');
    this.setCurrentModalState('email');
  }

  closeLoginModal() {
    if (!this.modalContainer) return;
    this.modalContainer.classList.remove('visible');
    document.body.classList.remove('smart-wallet-login-opened');
    this.setCurrentModalState('email');
  }

  private isModalContainerInjected() {
    return !!this.modalContainer && !!document.getElementById('login-modal-container');
  }

  private injectModalContainer() {
    // Verificar primeiro se já existe no DOM (mais confiável que apenas a propriedade da instância)
    const existingContainer = document.getElementById('login-modal-container');
    if (existingContainer) {
      // Se já existe no DOM, sincronizar a referência da instância atual
      if (!this.modalContainer) {
        this.modalContainer = existingContainer as HTMLDivElement;
      }
      return;
    }

    // Verificar se outra instância está criando (via localStorage)
    if (this.isModalContainerInjecting || localStorage.getItem('isModalContainerInjecting') === 'true') {
      return;
    }

    try {
      this.isModalContainerInjecting = true;
      localStorage.setItem('isModalContainerInjecting', 'true');

      // Double-check: verificar novamente após definir a flag (evita race condition)
      const doubleCheck = document.getElementById('login-modal-container');
      if (doubleCheck) {
        this.modalContainer = doubleCheck as HTMLDivElement;
        this.isModalContainerInjecting = false;
        localStorage.removeItem('isModalContainerInjecting');
        return;
      }

      this.modalContainer = document.createElement('div');
      this.modalContainer.id = 'login-modal-container';
      document.body.appendChild(this.modalContainer);

      this.modalContainer.classList.add('login-modal');

      // add styles to the modal container
      const styles = `
        #login-modal-container.login-modal {
          background-image: url(assets/images/bg-map.png) !important;
          background-size: cover;
          // backdrop-filter: blur(10px);
          // background-color: #00000066;
          background-color: transparent;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 999999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease-in-out;
        }
  
        #login-modal-container .login-modal-dialog {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, 50%);
          background-color: rgba(50, 50, 50, 0.5);
          backdrop-filter: blur(10px);
          padding: 20px;
          border-radius: 10px;
          color: #fff;
          text-align: center;
          min-width: 300px;
          max-width: 90vw;
          transition: all 0.3s ease-in-out;
          transition-delay: 0.3s;
        }
        #login-modal-container.login-modal.visible {
          opacity: 1;
          pointer-events: auto;
        }
  
        #login-modal-container input, 
        #login-modal-container textarea, 
        #login-modal-container select {
          font-size: 18px !important;
        }
  
        #login-modal-container.login-modal.visible .login-modal-dialog {
          transform: translate(-50%, -50%);
        }
  
        #login-modal-container .login-modal-dialog .logo {
          margin-top: 20px;
          height: 25px;
        }
        #login-modal-container .login-modal-content p {
          font-size: 16px;
          margin-bottom: 10px;
        }
        #login-modal-container .login-modal-content input {
          width: 100%;
          padding: 10px 15px;
          font-size: 16px;
          border-radius: 10px;
          border: 1px solid #fff;
          color: #fff;
          background-color: #222;
          margin-bottom: 10px;
        }
  
        #login-modal-container .login-modal-content button {
          width: 100%;
          padding: 15px;
          border-radius: 10px;
          border: none;
          margin-top: 10px;
          font-size: 16px;
          background-color: #fff;
          color: #000;
        }
        
        #login-modal-container .login-modal-content button#login-button {
          background-color: transparent;
          color: #fff;
          border: 1px solid #fff;
        }
          
        #login-modal-container .login-modal-content button:hover {
          color: #fff;
          background-color: #000;
        }
  
        #login-modal-container .login-modal-content button:disabled {
          background-color: #ccc;
          color: #000;
          cursor: not-allowed;
        }
  
        #login-modal-container .login-modal-content.hidden {
          display: none;
        }
        #login-modal-container .login-modal-footer {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translate(-50%, 100%);
          width: 100%;
          text-align: center;
          font-size: 12px;
          color: #fff;
          margin-top: 10px;
        }
        #login-modal-container .login-modal-footer .powered-by {
          // display: flex;
          display: none;
          margin-top: 10px;
          justify-content: center;
          align-items: center;
          gap: 15px;
        }
        #login-modal-container small {
          font-size: 12px;
        }
        #login-modal-container small a {
          font-size: 12px;
          color: #CCC;
          text-decoration: underline;
        }
        #login-modal-container .login-modal-footer .powered-by .arbitrum-logo {
          height: 30px;
        }
        #login-modal-container .login-modal-footer .rivian-logo {
          margin-top: 10px;
          height: 80px;
        }

        div#credential_picker_container,
        iframe#credential_picker_iframe{
          z-index: 10000001 !important;
        }
        
        #login-modal-container .alert-popup {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background-color: rgba(220, 53, 69, 0.95);
          backdrop-filter: blur(10px);
          padding: 20px 30px;
          border-radius: 12px;
          color: #fff;
          text-align: center;
          min-width: 280px;
          max-width: 90vw;
          z-index: 10000000;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        
        #login-modal-container .alert-popup.visible {
          opacity: 1;
          pointer-events: auto;
          transform: translate(-50%, -50%) scale(1);
        }
        
        #login-modal-container .alert-popup .alert-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        #login-modal-container .alert-popup .alert-message {
          font-size: 16px;
          margin-bottom: 20px;
          line-height: 1.5;
        }
        
        #login-modal-container .alert-popup .alert-button {
          background-color: #fff;
          color: #dc3545;
          border: none;
          padding: 10px 25px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        #login-modal-container .alert-popup .alert-button:hover {
          background-color: #f8f9fa;
        }
      `;

      const styleElement = document.createElement('style');
      styleElement.textContent = styles;
      document.head.appendChild(styleElement);

      this.modalContainer.innerHTML = `
      <div class="login-modal-dialog">
        <img src="https://sponstar.io/assets/logos/sponstar-logo-home.svg?v2" height="40" alt="logo" class="logo" />
        <div id="login-loading" class="login-modal-content">
          <p>Loading... Please wait while we log you in...</p>
        </div>
        <div id="login-modal" class="login-modal-content">
            <p>Enter the SXSW 26 Treasure Hunt!  Create an account or login by providing your email</p>
            <button id="google-login-button">Continue with Google</button>
            <p style="opacity:0.8;margin:12px 0;">or</p>
            <input id="login-email-input" type="email" placeholder="Email" />
            <button id="login-button">Continue with Email</button>
            
          </div>
          <div id="otp-modal" class="login-modal-content">
            <p>Login by entering the One time Password, in your email inbox</p>
            <input id="otp-input" type="tel" placeholder="Type here..." />
            <button id="otp-button">Confirm</button>
          </div>
          <small>By logging in, you agree to our <a href="https://www.alchemy.com/terms-conditions/end-user-terms" target="_blank">Terms of Use</a></small>
          <div class="login-modal-footer">
            <div class="powered-by">Powered by <img src="https://arbitrum.io/logo_horizontal_white.svg" alt="arbitrum-logo" class="arbitrum-logo" /></div>
            <div class="rivian-logo">
              <img src="assets/logos/starly-login.png" alt="rivian-logo" class="rivian-logo" />
            </div>

          </div>
          <div id="alert-popup" class="alert-popup">
            <div class="alert-icon">⚠️</div>
            <div class="alert-message"></div>
            <button class="alert-button">OK</button>
          </div>
        </div>
      `;

      const googleButton = document.getElementById('google-login-button');
      googleButton?.addEventListener(
        'click',
        () => {
          this.startGoogleAuth();
        },
        { once: false },
      );

      const loginButton = document.getElementById('login-button');
      loginButton?.addEventListener(
        'click',
        () => {
          this.setCurrentModalState('loading');
          const emailInput = document.getElementById('login-email-input') as HTMLInputElement;
          this.handleEmailLogin(emailInput.value);
        },
        { once: false },
      );

      const otpButton = document.getElementById('otp-button');
      otpButton?.addEventListener(
        'click',
        async () => {
          this.setCurrentModalState('loading');
          const otpInput = document.getElementById('otp-input') as HTMLInputElement;
          await this.handleOtpConfirm(otpInput.value);
        },
        { once: false },
      );

      const emailInput = document.getElementById('login-email-input') as HTMLInputElement;
      emailInput.addEventListener(
        'touchend',
        () => {
          setTimeout(() => emailInput.focus(), 0);
        },
        { once: true },
      );

      const alertButton = document.getElementById('alert-popup')?.querySelector('.alert-button');
      alertButton?.addEventListener('click', () => {
        this.hideAlert();
      });
    } catch (error) {
      console.error('[SmartWalletService] injectModalContainer error', error);
    } finally {
      this.isModalContainerInjecting = false;
      localStorage.removeItem('isModalContainerInjecting');
    }
  }

  private setCurrentModalState(modal: 'email' | 'otp' | 'loading') {
    const loginModal = document.getElementById('login-modal');
    const otpModal = document.getElementById('otp-modal');
    const loginLoading = document.getElementById('login-loading');
    loginModal?.classList.add('hidden');
    otpModal?.classList.add('hidden');
    loginLoading?.classList.add('hidden');
    if (modal === 'email') {
      loginModal?.classList.remove('hidden');
    } else if (modal === 'otp') {
      otpModal?.classList.remove('hidden');
    } else if (modal === 'loading') {
      loginLoading?.classList.remove('hidden');
    }
  }

  async googleSignIn(googleResponse: any) {
    try {
      const { ok, user } = await this.verifyCredential(googleResponse?.credential);
      if (!ok || !user) {
        throw new Error('Google credential verification failed');
      }
      this.runInAngularZone(() => {
        this.isAuthenticated$.next(true);
        this.authService.isAuthenticated$.next(true); // Sync with AuthServiceApp
        this.closeLoginModal();
        this.setCurrentModalState('email');
      });
      console.log('[SmartWalletService] googleSignIn success', user);
    } catch (e) {
      console.error('[SmartWalletService] googleSignIn error', e);
      this.setCurrentModalState('email');
    }
  }

  private async handleEmailLogin(email: string) {
    try {
      const { ok } = await this.requestOtp(email);
      if (!ok) {
        throw new Error('OTP request failed');
      }
      localStorage.setItem('loginEmail', email);
      this.setCurrentModalState('otp');
    } catch (e) {
      console.error('[SmartWalletService] requestOtp error', e);
      this.setCurrentModalState('email');
    }
  }

  private async handleOtpConfirm(otp: string) {
    try {
      const email = localStorage.getItem('loginEmail') || '';
      if (!email) throw new Error('Missing email context');
      const { ok } = await this.verifyOtp(email, otp, this.pendingAuthProviderId);
      if (!ok) {
        this.showAlert('OTP verification failed. Please check your code and try again.');
        // Clear the OTP input
        const otpInput = document.getElementById('otp-input') as HTMLInputElement;
        if (otpInput) otpInput.value = '';
        this.setCurrentModalState('email');
        return;
      }
      this.pendingAuthProviderId = undefined;
      localStorage.removeItem('loginEmail');
      this.runInAngularZone(() => {
        this.isAuthenticated$.next(true);
        this.authService.isAuthenticated$.next(true); // Sync with AuthServiceApp
        this.closeLoginModal();
      });
    } catch (e) {
      console.error('[SmartWalletService] verifyOtp error', e);
      this.showAlert('OTP verification failed. Please check your code and try again.');
      // Clear the OTP input
      const otpInput = document.getElementById('otp-input') as HTMLInputElement;
      if (otpInput) otpInput.value = '';
      this.setCurrentModalState('email');
    }
  }

  private startGoogleAuth() {
    try {
      this.setCurrentModalState('loading');
      const clientId = environment.googleClientId as any;
      const gsi = (window as any)?.google?.accounts?.id;
      if (!clientId || !gsi) {
        console.warn('[SmartWalletService] Google Identity not available or clientId missing; dispatching integration event');
        try {
          const evt = new CustomEvent('sponstar:google-login-click', {
            detail: {
              clientId: environment.googleClientId,
              clientSecret: (environment as any).authGoogle, // for native/webview backends to exchange code server-side
            },
          });
          window.dispatchEvent(evt);
        } catch {}
        return;
      }

      gsi.initialize({
        client_id: clientId,
        ux_mode: 'popup',
        use_fedcm_for_prompt: true,
        use_fedcm_for_button: true,
        callback: (response: any) => {
          console.log('[SmartWalletService] Google popup callback', response);
          try {
            if (response?.credential) {
              this.googleSignIn({ credential: response.credential });
              return;
            }
          } catch (e) {
            console.error('[SmartWalletService] GIS callback error', e);
          }
          // User closed popup without completing login - restore modal state
          this.setCurrentModalState('email');
          // Fallback to integration event if no credential is provided
          try {
            const evt = new CustomEvent('sponstar:google-login-click', {
              detail: {
                clientId: environment.googleClientId,
                clientSecret: (environment as any).authGoogle,
              },
            });
            window.dispatchEvent(evt);
          } catch {
            console.error('[SmartWalletService] Google popup closed');
            this.setCurrentModalState('email');
          }
        },
        onClose: () => {
          console.log('[SmartWalletService] Google popup closed');
          this.setCurrentModalState('email');
        },
        intermediate_iframe_close_callback: () => {
          console.log('[SmartWalletService] Intermediate iframe closed');
          this.setCurrentModalState('email');
        },
      });
      gsi.prompt((notification: any) => {
        // Replace isDismissedMoment() with FedCM-compatible check
        const dismissedReason = notification.getDismissedReason();
        const skippedReason = notification.getSkippedReason();

        if (dismissedReason) {
          console.log('[SmartWalletService] Google modal dismissed:', dismissedReason);
          this.setCurrentModalState('email');
        } else if (skippedReason) {
          console.log('[SmartWalletService] Google modal skipped:', skippedReason);
          this.setCurrentModalState('email');
        }
      }); // triggers account chooser/One Tap if available
    } catch (e) {
      console.error('[SmartWalletService] startGoogleAuth error', e);
      this.setCurrentModalState('email');
      // Last-resort fallback event for host/native layers
      try {
        const evt = new CustomEvent('sponstar:google-login-click', {
          detail: {
            clientId: environment.googleClientId,
            clientSecret: (environment as any).authGoogle,
          },
        });
        window.dispatchEvent(evt);
      } catch {
        this.setCurrentModalState('email');
      }
    }
  }

  private showAlert(message: string) {
    const alertPopup = document.getElementById('alert-popup');
    const alertMessage = alertPopup?.querySelector('.alert-message');
    if (alertPopup && alertMessage) {
      alertMessage.textContent = message;
      alertPopup.classList.add('visible');
      // Auto-hide after 5 seconds
      setTimeout(() => {
        this.hideAlert();
      }, 5000);
    }
  }

  private hideAlert() {
    const alertPopup = document.getElementById('alert-popup');
    if (alertPopup) {
      alertPopup.classList.remove('visible');
    }
  }

  private async requestOtp(email: string): Promise<{ ok: boolean; expiresAt: number }> {
    const res: any = await firstValueFrom(this.msService.post('auth-service/login', { email }));
    return { ok: !!res?.ok, expiresAt: res?.expiresAt };
  }

  private async verifyOtp(email: string, nonce: string, authProviderId?: string): Promise<{ ok: boolean }> {
    const res: any = await firstValueFrom(this.msService.post('auth-service/otp-verify', { email, nonce, authProviderId }));
    if (res?.ok && res?.accessToken) {
      localStorage.setItem('accessToken', res.accessToken);
      if (res?.refreshToken) localStorage.setItem('refreshToken', res.refreshToken);
      if (res?.user) localStorage.setItem('user', JSON.stringify(res.user));
      this.runInAngularZone(() => {
        this.isAuthenticated$.next(true);
        this.authService.isAuthenticated$.next(true); // Sync with AuthServiceApp
      });
      return { ok: true };
    }
    return { ok: false };
  }

  private async verifyCredential(
    idToken: string,
  ): Promise<{ ok: boolean; sub?: string; picture?: string; email?: string; token?: string; user?: any }> {
    try {
      const res: any = await firstValueFrom(this.msService.post('auth-service/verify-credential', { idToken, provider: 'google' }));
      // Expecting backend to respond with { ok, sub, email, token?, user? }
      if (res?.ok && res?.accessToken) {
        localStorage.setItem('accessToken', res.accessToken);
        if (res?.user) localStorage.setItem('user', JSON.stringify(res.user));
        this.runInAngularZone(() => {
          this.isAuthenticated$.next(true);
          this.authService.isAuthenticated$.next(true); // Sync with AuthServiceApp
        });
      }
      return {
        ok: !!res?.ok,
        sub: res?.sub,
        picture: res?.picture,
        email: res?.email,
        token: res?.token,
        user: res?.user,
      };
    } catch (e) {
      return { ok: false };
    }
  }

  logout() {
    // Clear all user data
    this.runInAngularZone(() => {
      localStorage.clear();
      this.isAuthenticated$.next(false);
      this.authService.isAuthenticated$.next(false); // Sync with AuthServiceApp
    });

    // Clear user data from UserService
    if (this.userService.user$) {
      this.userService.user$.next(null);
    }

    // Open login modal
    this.openLoginModal();
  }

  private convertBigIntToString(value: any): any {
    if (typeof value === 'bigint') {
      return value.toString();
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.convertBigIntToString(item));
    }
    if (value && typeof value === 'object') {
      const converted: any = {};
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          converted[key] = this.convertBigIntToString(value[key]);
        }
      }
      return converted;
    }
    return value;
  }

  async call(contractName: string, methodName: string, args: any[]): Promise<any> {
    if (!this.web3) throw new Error('No web3 Instance');
    try {
      const contract = new this.web3.eth.Contract(this.contracts[contractName].abi, this.contracts[contractName].address);
      // Convert BigInt values to strings for Web3.js compatibility (recursively handle nested structures)
      const convertedArgs = args.map((arg) => this.convertBigIntToString(arg));
      const result = await contract.methods[methodName](...convertedArgs).call();
      // Convert BigInt values in the return value to strings
      return this.convertBigIntToString(result);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async approve(contractName: string, methodName: string, amount: string): Promise<any> {
    if (!this.web3) throw new Error('No web3 Instance');
    try {
      const contract = new this.web3.eth.Contract(this.contracts[contractName].abi, this.contracts[contractName].address);
      return await contract.methods[methodName](amount).call();
    } catch (error) {
      console.error(error);
    }
  }

  async send(contractName: string, methodName: string, args: any[]): Promise<any> {
    if (!this.web3) throw new Error('No web3 Instance');
    try {
      const contract = new this.web3.eth.Contract(this.contracts[contractName].abi, this.contracts[contractName].address);
      // Convert BigInt values to strings for Web3.js compatibility
      const convertedArgs = args.map((arg) => (typeof arg === 'bigint' ? arg.toString() : arg));
      return await contract.methods[methodName](...convertedArgs).call();
    } catch (error) {
      console.error(error);
    }
  }
}
